import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { TagInfo } from "../data/api";

import "./DrawingsDashboard.scss";

interface DrawingCardProps {
  id: string;
  name: string;
  thumbnail?: string | null;
  updatedAt: string | number;
  tags?: TagInfo[];
  selectMode?: boolean;
  selected?: boolean;
  onSelect?: (id: string) => void;
  onOpen: (id: string) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  onRename: (id: string, newName: string) => void;
  onManageTags?: (id: string) => void;
  onMoveTo?: (id: string) => void;
  onShareViewLink?: (id: string) => void;
  onShareInternal?: (id: string, name: string) => void;
  onManageAccess?: (id: string, name: string) => void;
}

function formatRelative(updatedAt: string | number): string {
  const date = new Date(updatedAt);
  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffH / 24);

  if (diffMin < 1) return "agora mesmo";
  if (diffMin < 60) return `Editado há ${diffMin} min`;
  if (diffH < 24) return `Editado há ${diffH} h`;
  if (diffDays < 7) return `Editado há ${diffDays} d`;
  if (diffDays < 30) return `Editado há ${Math.floor(diffDays / 7)} sem`;
  if (diffDays < 365) return `Editado há ${Math.floor(diffDays / 30)} mês`;
  return `Editado há ${Math.floor(diffDays / 365)} ano`;
}

export const DrawingCard = ({
  id,
  name,
  thumbnail,
  updatedAt,
  tags = [],
  selectMode = false,
  selected = false,
  onSelect,
  onOpen,
  onDelete,
  onDuplicate,
  onRename,
  onManageTags,
  onMoveTo,
  onShareViewLink,
  onShareInternal,
  onManageAccess,
}: DrawingCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(name);
  const [showMenu, setShowMenu] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);

  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const MENU_WIDTH = 200;

  // Position the portal menu under the trigger button, anchored to the right.
  useLayoutEffect(() => {
    if (!showMenu || !menuBtnRef.current) {
      setMenuPos(null);
      return;
    }
    const rect = menuBtnRef.current.getBoundingClientRect();
    const top = rect.bottom + 6;
    let left = rect.right - MENU_WIDTH;
    // Keep menu inside viewport
    if (left < 8) left = 8;
    const maxLeft = window.innerWidth - MENU_WIDTH - 8;
    if (left > maxLeft) left = maxLeft;
    setMenuPos({ top, left });
  }, [showMenu]);

  // Close menu on outside click, scroll, or resize.
  useEffect(() => {
    if (!showMenu) return;
    const close = (e: MouseEvent) => {
      if (
        menuRef.current?.contains(e.target as Node) ||
        menuBtnRef.current?.contains(e.target as Node)
      ) {
        return;
      }
      setShowMenu(false);
    };
    const onScroll = () => setShowMenu(false);
    document.addEventListener("mousedown", close);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", close);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [showMenu]);

  const handleRename = () => {
    if (editName.trim() && editName !== name) {
      onRename(id, editName.trim());
    }
    setIsEditing(false);
  };

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData("application/x-drawing-id", id);
    e.dataTransfer.effectAllowed = "move";
    const el = e.currentTarget as HTMLElement;
    el.classList.add("drawing-card--dragging");
  };

  const handleDragEnd = (e: React.DragEvent) => {
    const el = e.currentTarget as HTMLElement;
    el.classList.remove("drawing-card--dragging");
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if (selectMode && onSelect) {
      e.stopPropagation();
      onSelect(id);
    }
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect?.(id);
  };

  return (
    <div
      className={`drawing-card ${selected ? "drawing-card--selected" : ""} ${selectMode ? "drawing-card--select-mode" : ""} ${showMenu ? "drawing-card--menu-open" : ""}`}
      onDoubleClick={() => !selectMode && onOpen(id)}
      onClick={handleCardClick}
      draggable={!selectMode}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="drawing-card__cover">
        {thumbnail ? (
          <img
            className="drawing-card__cover-img"
            src={thumbnail}
            alt={name}
            draggable={false}
          />
        ) : (
          <div className="drawing-card__cover-placeholder">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M3 14l5-5 4 4 3-3 6 6" />
              <circle cx="8.5" cy="8.5" r="1.2" fill="currentColor" stroke="none" />
            </svg>
          </div>
        )}

        <div className="drawing-card__checkbox" onClick={handleCheckboxClick}>
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onSelect?.(id)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>

        <div className="drawing-card__actions">
          <button
            ref={menuBtnRef}
            className="drawing-card__menu-btn"
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(!showMenu);
            }}
            title="Menu"
          >
            &#8942;
          </button>
        </div>
      </div>

      <div className="drawing-card__info">
        {isEditing ? (
          <input
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={handleRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleRename();
              }
              if (e.key === "Escape") {
                setIsEditing(false);
                setEditName(name);
              }
            }}
            autoFocus
            className="drawing-card__name-input"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span
            className="drawing-card__name"
            onDoubleClick={(e) => {
              e.stopPropagation();
              setIsEditing(true);
            }}
            title={name}
          >
            {name}
          </span>
        )}
        <span className="drawing-card__date">{formatRelative(updatedAt)}</span>
        {tags.length > 0 && (
          <div className="drawing-card__tags">
            {tags.slice(0, 3).map((tag) => (
              <span
                key={tag.id}
                className="drawing-card__tag"
                style={{ backgroundColor: tag.color + "22", color: tag.color, borderColor: tag.color + "44" }}
              >
                {tag.name}
              </span>
            ))}
            {tags.length > 3 && (
              <span className="drawing-card__tag drawing-card__tag--more">
                +{tags.length - 3}
              </span>
            )}
          </div>
        )}
      </div>

      {showMenu && menuPos &&
        createPortal(
          <div
            ref={menuRef}
            className="drawing-card__menu drawing-card__menu--portal"
            style={{ top: menuPos.top, left: menuPos.left, width: MENU_WIDTH }}
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={() => { onOpen(id); setShowMenu(false); }}>
              Abrir
            </button>
            <button
              onClick={() => {
                setIsEditing(true);
                setShowMenu(false);
              }}
            >
              Renomear
            </button>
            <button onClick={() => { onDuplicate(id); setShowMenu(false); }}>
              Duplicar
            </button>
            {onShareInternal && (
              <button onClick={() => { onShareInternal(id, name); setShowMenu(false); }}>
                Compartilhar com pessoas
              </button>
            )}
            {onManageAccess && (
              <button onClick={() => { onManageAccess(id, name); setShowMenu(false); }}>
                Pessoas com acesso
              </button>
            )}
            {onShareViewLink && (
              <button onClick={() => { onShareViewLink(id); setShowMenu(false); }}>
                Link de visualização
              </button>
            )}
            {onMoveTo && (
              <button onClick={() => { onMoveTo(id); setShowMenu(false); }}>
                Mover para...
              </button>
            )}
            {onManageTags && (
              <button onClick={() => { onManageTags(id); setShowMenu(false); }}>
                Tags
              </button>
            )}
            <button
              className="danger"
              onClick={() => { onDelete(id); setShowMenu(false); }}
            >
              Excluir
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
};
